import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../../api/useApi";
import { request } from "../../../api";
import Spinner from "../../Loaders/Spinner";
import { standardsList, standardNameSuggestions } from "../../../api/ruleQueries";
import { tagsList } from "../../../api/tagQueries";
import { setRuleStandardEnabledFlag } from "../../../api/ruleMutations";
import ErrorMessages from "../../Notifications/ErrorMessages";
import { Link } from "react-router-dom";
import Button from "../../Button";
import { filterDisabled } from "../../../common/helpers/util";
import Modal from "../../Modal";
import DataTable from "../../Table/DataTable";
import { ruleStandardById } from "../../../common/paths";
import usePin from "../../Pin/usePin";
import RowActionsMenu, { MenuItem } from "../../Table/RowActionsMenu";
import columnSort from "../../Table/helpers/columnSort";
import StyledLink from "../../StyledLink";
import { sortCitations } from "../../Citations/citationHelpers";
import CitationLink from "../../Citations/CitationLink";
import TextColumnFilter from "../../Table/TextColumnFilter";
import textFilterToWhereClause from "../../Table/helpers/textFilterToWhere";
import { MdDelete, MdPushPin } from "react-icons/md";
import styled from "styled-components/macro";

// Neutral tag chip (FR-13): all tags render in one muted style regardless of
// name or category — light-gray background, muted-gray text, subtle border.
const TagChip = styled.span`
  display: inline-block;
  background: #ffffff;
  color: #374151;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
`;

// Muted amber badge for policies with > 10 draft mappings (FR-8).
const DraftBadge = styled.span`
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  padding: 0.1rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
`;

// Scopes the row-hover UX to the Policies table only: light-blue row tint and
// the policy name underlining on hover. (The row-actions kebab is always
// visible, so there's no hover-reveal here.)
const TableWrap = styled.div`
  tbody tr:hover td {
    background: #eff6ff;
  }
  tbody tr:hover [data-testid="policyLink"] {
    text-decoration: underline;
  }
`;

// Row-action menu item that pins/unpins a policy ("quick access"). Backed by
// the shared usePin hook so it stays in sync with PinContext + localStorage.
const PinMenuItem = ({ id }) => {
  const { isPinned, toggle } = usePin("policies", id);
  return (
    <MenuItem type="button" onClick={toggle}>
      <MdPushPin />
      {isPinned ? "Remove from Quick Access" : "Add to Quick Access"}
    </MenuItem>
  );
};

// Map the column-menu operator keys to the GraphQL TagNameFilterOperator enum
// values (HotChocolate emits UPPER_SNAKE_CASE) for the Tags filter (FR-5).
const TAG_OPERATOR_TO_ENUM = {
  eq: "EQUALS",
  neq: "NOT_EQUALS",
  contains: "CONTAINS",
  ncontains: "NOT_CONTAINS",
  empty: "IS_EMPTY",
  nempty: "IS_NOT_EMPTY",
  in: "IN_LIST",
  nin: "NOT_IN_LIST",
};

// Build the CustomTagFilterInput from the Tags column-menu filter value (FR-5).
const buildTagFilter = (tagsFilterValue) => {
  const base = { tagIds: [], exactMatch: false };
  if (!tagsFilterValue) {
    return base;
  }
  const nameOperator = TAG_OPERATOR_TO_ENUM[tagsFilterValue.operator];
  if (!nameOperator) {
    return base;
  }
  return { ...base, nameOperator, nameValues: tagsFilterValue.values ?? [] };
};

const DataSourceRulesListWidget = () => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [ruleToDelete, setruleToDelete] = useState(null);

  //Init Data Fetch
  const [
    {
      loading: availableStandardsLoading,
      errors: availableStandardsErrors,
      data: standards,
    },
    doFetch,
  ] = useApi();

  const totalCount = standards?.availableBusinessRuleStandards?.totalCount;
  const pageInfo = standards?.availableBusinessRuleStandards?.pageInfo;

  //Fetch for Table Paged
  const fetchData = React.useCallback(
    ({ pageSize, cursor, sortBy, filters }) => {
      const sortedObject = columnSort(sortBy);

      // Base where + per-column filters from the column menu (FR-4). Name maps
      // to the auto-generated `where` string ops; Tags is handled via tagFilter
      // (Component 4).
      const where = { enabled: { eq: true } };
      const nameFilter = filters?.find((f) => f.id === "name");
      if (nameFilter?.value) {
        const clause = textFilterToWhereClause(nameFilter.value);
        if (clause) where.name = clause;
      }

      const tagsFilter = filters?.find((f) => f.id === "tags");

      doFetch({
        query: standardsList,
        variables: {
          first: pageSize,
          tagFilter: buildTagFilter(tagsFilter?.value),
          after: cursor,
          standardType: "RULE",
          where,
          ...(sortedObject
            ? {
                order: {
                  ...sortedObject,
                },
              }
            : {
                order: {
                  name: "ASC",
                },
              }),
        },
      });
    },
    [doFetch]
  );

  // Distinct-value autocomplete for the column filters (FR: suggest after 2
  // chars). Live server lookups so suggestions span all pages, not just the
  // loaded one. Names come from a slim standards query; Tags from allTags.
  const suggestNames = useCallback(async (term) => {
    const { data } = await request(standardNameSuggestions, {
      first: 8,
      tagFilter: { tagIds: [], exactMatch: false },
      standardType: "RULE",
      where: { enabled: { eq: true }, name: { contains: term } },
      order: { name: "ASC" },
    });
    const edges = data?.availableBusinessRuleStandards?.edges ?? [];
    return [...new Set(edges.map((e) => e?.node?.name).filter(Boolean))];
  }, []);

  const suggestTags = useCallback(async (term) => {
    const { data } = await request(tagsList, {
      where: { name: { contains: term } },
      order: { name: "ASC" },
    });
    return [
      ...new Set((data?.allTags ?? []).map((t) => t?.name).filter(Boolean)),
    ];
  }, []);

  useEffect(() => {
    if (ruleToDelete) {
      setShowConfirm(true);
    } else {
      setShowConfirm(false);
    }
  }, [ruleToDelete]);

  //remove rules state

  const [
    { loading: removeRuleDataLoading, data: removeRuleData },
    removeRuleApi,
  ] = useApi();

  //handle remove rule update
  useEffect(() => {
    if (removeRuleData) {
      //set filtered rule
      setRules((prevRules) =>
        prevRules.filter(
          (rule) =>
            rule?.node?.id !== removeRuleData?.setRuleStandardEnabledFlag?.id
        )
      );
      setruleToDelete(null);
    }
  }, [removeRuleData]);

  //define initial rule state
  const [rules, setRules] = useState([]);

  //callback for removing a row
  const removeRuleStandard = useCallback(
    (templateId) => {
      const variables = { id: templateId, enabled: false };
      removeRuleApi({ query: setRuleStandardEnabledFlag, variables });
    },
    [removeRuleApi]
  );

  //set rules into local state so we can mutate on remove
  useEffect(() => {
    if (standards) {
      const availableStandards =
        standards?.availableBusinessRuleStandards?.edges ?? [];
      setRules(filterDisabled(availableStandards));
    }
  }, [standards]);

  if (availableStandardsErrors)
    return <ErrorMessages errors={availableStandardsErrors} />;

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.name,
      // Column menu (AC-15377): search + A–Z/Z–A sort + 8-operator text filter.
      // Name is server-sortable (has an accessor → canSort) and maps to the
      // auto-generated `where` string ops via textFilterToWhereClause.
      enableColumnMenu: true,
      filterType: "text",
      // Name is NotNull, so Is Empty / Is Not Empty are meaningless — omit them.
      renderFilterMenu: (col) => (
        <TextColumnFilter
          column={col}
          operators={["eq", "neq", "contains", "ncontains", "in", "nin"]}
          suggest={suggestNames}
        />
      ),
      Cell: ({ row: { original } }) => {
        return (
          <StyledLink
            to={ruleStandardById(original?.node?.id)}
            data-testid="policyLink"
          >
            {original?.node?.name}
          </StyledLink>
        );
      },
    },
    {
      Header: "Instances Mapped",
      id: "PIM",
      // Display-only derived count (FR-9): not server-sortable/filterable.
      disableSortBy: true,
      // Numeric column: right-align header + value so they line up.
      align: "right",
      Cell: ({ row: { original } }) => {
        const instances = original?.node?.instances.filter(
          (ni) =>
            ni?.enabledState !== "DRAFT" &&
            ni?.enabledState !== "DISABLED" &&
            ni?.dataSource?.enabled
        );

        return <div style={{ textAlign: "right" }}>{instances?.length}</div>;
      },
    },
    {
      Header: "Draft Instances",
      id: "PDM",
      // Display-only derived count (FR-9): not server-sortable/filterable.
      disableSortBy: true,
      // Numeric column: right-align header + value so they line up.
      align: "right",
      Cell: ({ row: { original } }) => {
        const count = original?.node?.instances.filter(
          (ni) =>
            ni?.enabledState !== "PUBLISHED" &&
            ni?.enabledState !== "DISABLED" &&
            ni?.dataSource?.enabled
        )?.length;

        // > 10 draft mappings: amber badge linking to the manage page (FR-8).
        return (
          <div style={{ textAlign: "right" }}>
            {count > 10 ? (
              <Link
                to={ruleStandardById(original?.node?.id)}
                title={`${count} draft mappings pending — click to review`}
              >
                <DraftBadge>{count}</DraftBadge>
              </Link>
            ) : (
              count
            )}
          </div>
        );
      },
    },
    {
      Header: "Tags",
      id: "tags",
      // Column menu (AC-15377): search + 8-operator text filter on tag name
      // (FR-5, server-side via CustomTagFilter.FilterByTagName). Sort is dropped
      // for Tags (multi-valued) — disableSortBy keeps canSort false so the menu
      // shows no sort options.
      enableColumnMenu: true,
      filterType: "text",
      disableSortBy: true,
      renderFilterMenu: (col) => (
        <TextColumnFilter column={col} suggest={suggestTags} />
      ),
      Cell: ({ row: { original } }) => {
        // Display only enabled, Policy-type tags so the chips match what the
        // tag-name filter actually matches (FilterByTagName scope).
        const tags = (original?.node?.tagInstances ?? []).filter(
          (ti) => ti?.enabled && ti?.type === "POLICY"
        );
        return (
          <div>
            {tags.map((ti, i) => {
              const isLast = tags.length - 1 <= i;
              return (
                <TagChip
                  key={ti?.id ?? i}
                  style={{
                    marginRight: isLast ? "" : ".5rem",
                    marginBottom: isLast ? "" : ".5rem",
                  }}
                >
                  {ti?.tag?.name}
                </TagChip>
              );
            })}
          </div>
        );
      },
    },
    {
      Header: "Citations",
      id: "citations",
      Cell: ({ row: { original } }) => {
        return (
          <div>
            {sortCitations(original?.node?.citations ?? []).map((c, i, arr) => {
              const isLast = arr.length - 1 <= i;
              return (
                <div
                  key={c?.id}
                  style={{
                    marginRight: isLast ? "" : ".5rem",
                    marginBottom: isLast ? "" : ".5rem",
                    fontSize: ".8rem",
                  }}
                >
                  <CitationLink title={c?.title} url={c?.url} />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      Header: "Actions",
      id: "actions",
      width: 120,
      disableSortBy: true,
      // Row actions live in a kebab (3-dots) menu (AC-15377). System policies
      // (id <= 0) get a disabled Remove with a tooltip.
      Cell: ({ row: { original } }) => {
        const id = original?.node?.id;
        const isSystemPolicy = !(id > 0);
        return (
          <div style={{ textAlign: "right" }}>
            <RowActionsMenu ariaLabel="Policy actions">
              <PinMenuItem id={id} />
              <MenuItem
                danger
                type="button"
                disabled={isSystemPolicy}
                title={
                  isSystemPolicy
                    ? "System policies can't be removed"
                    : undefined
                }
                onClick={isSystemPolicy ? undefined : () => setruleToDelete(id)}
              >
                <MdDelete /> Remove
              </MenuItem>
            </RowActionsMenu>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {showConfirm ? (
        <Modal
          title={`Confirm Policy Removal`}
          hide={() => setruleToDelete(null)}
        >
          <p>Are you sure you wish to remove this Policy?</p>
          <div>
            <Button
              type="button"
              list="true"
              disabled={removeRuleDataLoading}
              danger
              onClick={() => removeRuleStandard(ruleToDelete)}
            >
              {removeRuleDataLoading ? <Spinner /> : "Yes"}
            </Button>
            <Button
              type="button"
              disabled={removeRuleDataLoading}
              onClick={() => {
                setruleToDelete(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      ) : null}

      <TableWrap>
        <DataTable
          defaultPageSize={10}
          fetchData={fetchData}
          loading={availableStandardsLoading}
          pageInfo={pageInfo}
          totalCount={totalCount}
          data={rules}
          columns={columnsData}
          reorderable
        />
      </TableWrap>
    </>
  );
};

export default DataSourceRulesListWidget;
